import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  legacyImportSummarySchema,
  legacyRunListSchema,
  type LegacyImportSummary,
} from '@competa/shared';
import { apiRequest, apiUrl } from '../../api/client';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { TextField } from '../../components/TextField';
import { errorMessage } from '../../i18n/errors';
import styles from '../competitions/Competitions.module.css';

/** Schlüssel der eigenen Importläufe im Query-Cache. */
const RUNS_KEY = ['import', 'runs'] as const;

/** Höchstzahl angezeigter Hinweise (der Rest steht im Protokoll). */
const MAX_ISSUES_SHOWN = 50;

/** Eine Vorschau mit der zugehörigen Datei (für die Bestätigung). */
interface Preview {
  file: File;
  summary: LegacyImportSummary;
}

/**
 * Altdaten-Import (Ausnahme, z. B. für die Erstbefüllung): Datei wählen → Vorschau (nichts wird gespeichert)
 * → Import bestätigen. Erkannt werden Flachliste, Matrix und Multi-Sheet (CSV oder XLSX); Vorlagen stehen zum
 * Download bereit. Darunter das eigene Importprotokoll.
 */
export function ImportPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [decidedAt, setDecidedAt] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [done, setDone] = useState<LegacyImportSummary | null>(null);

  const runs = useQuery({
    queryKey: RUNS_KEY,
    queryFn: () => apiRequest('GET', '/import/runs', { schema: legacyRunListSchema }),
  });
  const upload = useMutation({
    mutationFn: ({ file, dryRun }: { file: File; dryRun: boolean }) => {
      const form = new FormData();
      form.append('file', file);
      const query = new URLSearchParams({ dryRun: String(dryRun) });
      if (decidedAt) query.set('decidedAt', decidedAt);
      return apiRequest('POST', `/import/legacy?${query.toString()}`, {
        form,
        schema: legacyImportSummarySchema,
      });
    },
  });

  /** Berechnet nach der Dateiauswahl die Vorschau. */
  async function handleFile(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setDone(null);
    setPreview(null);
    upload.reset();
    try {
      setPreview({ file, summary: await upload.mutateAsync({ file, dryRun: true }) });
    } catch {
      // Der Fehler steht in `upload.error` und wird unten angezeigt.
    }
    await queryClient.invalidateQueries({ queryKey: RUNS_KEY });
  }

  /** Führt den Import mit der zuvor geprüften Datei tatsächlich aus. */
  async function confirm(): Promise<void> {
    if (!preview) return;
    try {
      setDone(await upload.mutateAsync({ file: preview.file, dryRun: false }));
      setPreview(null);
    } catch {
      // Fehleranzeige über `upload.error`.
    }
    await queryClient.invalidateQueries({ queryKey: RUNS_KEY });
    await queryClient.invalidateQueries({ queryKey: ['competitions'] });
    await queryClient.invalidateQueries({ queryKey: ['images'] });
  }

  const summary = preview?.summary;
  const failed = summary?.issues.some((issue) => issue.level === 'error') ?? false;

  return (
    <section>
      <h1>{t('import.title')}</h1>
      <p>{t('import.intro')}</p>
      <p>
        {t('import.templates')}{' '}
        {(['flat', 'matrix', 'multisheet'] as const).map((format) => (
          <span key={format} className={styles.check}>
            <a href={apiUrl(`/import/templates/${format}`)}>{t(`import.format.${format}`)}</a>
          </span>
        ))}
      </p>

      <div className={styles.toolbar}>
        <TextField
          label={t('import.decidedAt')}
          hint={t('import.decidedAtHint')}
          type="date"
          value={decidedAt}
          onChange={(event) => setDecidedAt(event.target.value)}
        />
        <input
          ref={input}
          type="file"
          accept=".csv,.xlsx"
          className={styles.hidden}
          aria-label={t('import.chooseFile')}
          onChange={(event) => void handleFile(event)}
        />
        <Button onClick={() => input.current?.click()} disabled={upload.isPending}>
          {t('import.chooseFile')}
        </Button>
      </div>

      {upload.isPending && (
        <p role="status">{preview ? t('import.importing') : t('import.previewing')}</p>
      )}
      {upload.isError && (
        <p role="alert" className={styles.error}>
          {errorMessage(t, upload.error)}
        </p>
      )}
      {done && (
        <p role="status" className={styles.success}>
          {t('import.done', {
            created: done.submissions.created,
            updated: done.submissions.updated,
          })}
        </p>
      )}

      {summary && preview && (
        <section aria-label={t('import.previewTitle', { file: preview.file.name })}>
          <h2>{t('import.previewTitle', { file: preview.file.name })}</h2>
          <p>{t('import.detected', { format: t(`import.format.${summary.format}`) })}</p>
          <dl>
            <dt>{t('import.records')}</dt>
            <dd>{summary.records}</dd>
            <dt>{t('import.images')}</dt>
            <dd>
              {t('import.createdExisting', {
                created: summary.images.created,
                existing: summary.images.existing,
              })}
            </dd>
            <dt>{t('import.competitions')}</dt>
            <dd>
              {t('import.createdExisting', {
                created: summary.competitions.created,
                existing: summary.competitions.existing,
              })}
              {' · '}
              {t('import.linked', { count: summary.competitions.linked })}
            </dd>
            <dt>{t('import.submissions')}</dt>
            <dd>
              {t('import.submissionCounts', {
                created: summary.submissions.created,
                updated: summary.submissions.updated,
                skipped: summary.submissions.skipped,
              })}
            </dd>
          </dl>
          <details open={summary.issues.length > 0}>
            <summary>{t('import.issues', { count: summary.issues.length })}</summary>
            {summary.issues.length === 0 ? (
              <p>{t('import.noIssues')}</p>
            ) : (
              <ul>
                {summary.issues.slice(0, MAX_ISSUES_SHOWN).map((issue, index) => (
                  <li key={`${issue.line}-${index}`}>
                    {issue.level === 'error' && <Badge tone="danger">{t('import.error')}</Badge>}{' '}
                    {issue.line ? `${t('import.line', { line: issue.line })}: ` : ''}
                    {issue.message}
                  </li>
                ))}
              </ul>
            )}
          </details>
          <div className={styles.actions}>
            <Button onClick={() => void confirm()} disabled={upload.isPending || failed}>
              {t('import.confirm')}
            </Button>
            <Button variant="secondary" onClick={() => setPreview(null)}>
              {t('common.cancel')}
            </Button>
          </div>
        </section>
      )}

      <h2>{t('import.log')}</h2>
      {runs.data && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('import.logWhen')}</th>
              <th>{t('import.logFile')}</th>
              <th>{t('import.logResult')}</th>
            </tr>
          </thead>
          <tbody>
            {runs.data.runs.length === 0 && (
              <tr>
                <td colSpan={3}>{t('import.logEmpty')}</td>
              </tr>
            )}
            {runs.data.runs.map((run) => (
              <tr key={run.id}>
                <td>{new Date(run.createdAt).toLocaleString(i18n.language)}</td>
                <td>{run.filename ?? '–'}</td>
                <td>
                  {run.failed ? (
                    <Badge tone="danger">{t('import.statusFailed')}</Badge>
                  ) : run.dryRun ? (
                    <Badge tone="neutral">{t('import.statusPreview')}</Badge>
                  ) : (
                    <Badge tone="success">{t('import.statusOk')}</Badge>
                  )}{' '}
                  {t('import.submissionCounts', {
                    created: run.inserted,
                    updated: run.updated,
                    skipped: run.skipped,
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
